export type AvatarSelection =
  | { type: 'preset'; value: string }
  | { type: 'custom'; value: string }

const STORAGE_PREFIX = 'ebookhub:avatar:'
const DIRTY_PREFIX = 'ebookhub:avatar:dirty:'

const PRESET_COLORS = [
  '#1677ff',
  '#13c2c2',
  '#52c41a',
  '#faad14',
  '#ff4d4f',
  '#722ed1',
  '#eb2f96',
  '#2f54eb',
  '#08979c',
  '#389e0d',
  '#d48806',
  '#cf1322',
]

const normalizeLabel = (v: unknown): string => {
  const s = String(v ?? '').trim()
  return s || 'U'
}

const getInitial = (label: string): string => {
  const s = normalizeLabel(label)
  const ch = Array.from(s)[0] || 'U'
  return ch.toUpperCase()
}

const hashToInt = (input: string): number => {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h >>> 0)
}

const encodeSvgDataUrl = (svg: string): string => {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

export const getAvatarPresets = (label: string): Array<{ id: string; src: string }> => {
  const initial = getInitial(label)
  return PRESET_COLORS.map((color, idx) => {
    const id = `p${idx + 1}`
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128"><rect width="128" height="128" rx="64" fill="${color}"/><text x="64" y="74" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans" font-size="56" font-weight="700" fill="white">${initial}</text></svg>`
    return { id, src: encodeSvgDataUrl(svg) }
  })
}

export const getDefaultPresetId = (seed: string | number | null | undefined): string => {
  const s = String(seed ?? '')
  const idx = hashToInt(s || 'default') % PRESET_COLORS.length
  return `p${idx + 1}`
}

export const readAvatarSelection = (userId: string | number | null | undefined): AvatarSelection | null => {
  if (!userId) return null
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${userId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { type?: string; value?: string }
    if (parsed?.type === 'preset' && typeof parsed.value === 'string') return { type: 'preset', value: parsed.value }
    if (parsed?.type === 'custom' && typeof parsed.value === 'string') return { type: 'custom', value: parsed.value }
    return null
  } catch {
    return null
  }
}

const setDirty = (userId: string | number, dirty: boolean): void => {
  if (!userId) return
  if (typeof window === 'undefined') return
  try {
    if (dirty) {
      window.localStorage.setItem(`${DIRTY_PREFIX}${userId}`, '1')
    } else {
      window.localStorage.removeItem(`${DIRTY_PREFIX}${userId}`)
    }
  } catch {
    void 0
  }
}

export const isAvatarSelectionDirty = (userId: string | number | null | undefined): boolean => {
  if (!userId) return false
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(`${DIRTY_PREFIX}${userId}`) === '1'
  } catch {
    return false
  }
}

export const markAvatarSelectionSynced = (userId: string | number | null | undefined): void => {
  if (!userId) return
  setDirty(userId, false)
}

export const writeAvatarSelection = (userId: string | number, sel: AvatarSelection, options?: { dirty?: boolean }): void => {
  if (!userId) return
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify(sel))
    if (options?.dirty !== false) setDirty(userId, true)
    try { window.dispatchEvent(new CustomEvent('ebookhub:avatar-changed', { detail: { userId, sel } })) } catch { void 0 }
  } catch {
    void 0
  }
}

export const clearAvatarSelection = (userId: string | number, options?: { dirty?: boolean }): void => {
  if (!userId) return
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${userId}`)
    if (options?.dirty !== false) setDirty(userId, true)
    try { window.dispatchEvent(new CustomEvent('ebookhub:avatar-changed', { detail: { userId, sel: null } })) } catch { void 0 }
  } catch {
    void 0
  }
}

export const getUserAvatarSrc = (userId: string | number | null | undefined, username: string | null | undefined): string | null => {
  const label = normalizeLabel(username)
  const sel = readAvatarSelection(userId)
  if (sel?.type === 'custom') return sel.value
  const presets = getAvatarPresets(label)
  const presetId = sel?.type === 'preset' ? sel.value : getDefaultPresetId(userId ?? username ?? 'default')
  const hit = presets.find(p => p.id === presetId) || presets[0]
  return hit?.src || null
}

export const fileToResizedAvatarDataUrl = async (file: File, size = 128): Promise<string> => {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = () => reject(new Error('load_failed'))
      i.src = url
    })

    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no_ctx')

    const w = img.naturalWidth || img.width || 1
    const h = img.naturalHeight || img.height || 1
    const side = Math.min(w, h)
    const sx = Math.max(0, Math.floor((w - side) / 2))
    const sy = Math.max(0, Math.floor((h - side) / 2))

    ctx.clearRect(0, 0, size, size)
    ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size)

    const tryWebp = (() => {
      try {
        const u = canvas.toDataURL('image/webp', 0.86)
        return typeof u === 'string' && u.startsWith('data:image/webp') ? u : null
      } catch {
        return null
      }
    })()

    return tryWebp || canvas.toDataURL('image/jpeg', 0.86)
  } finally {
    try { URL.revokeObjectURL(url) } catch { void 0 }
  }
}
